import axios, { AxiosResponse } from 'axios'
import hash from 'object-hash'

import {
  CompassAPIDataPoint,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  convertCompassToHighchartsStrCategoryFormat,
  getAreaRangeData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import {
  defaultMSDIndicators,
  defaultMinMaxIndicators,
  range6m1yAll,
} from '../utils/config'
import { MONTH_LIST_SHORT, Palette, chartPalette } from '../utils/constants'
import { capitalizeString, formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

const getRatioChartsOptions = (chartTitle: string) => ({
  addMinMaxIndicators: true,
  chartTitle,
  msdIndicators: { avg: true, sd1: true, sd2: true },
  msdIndicatorsSeriesIndexes: [0],
  yAxis: [
    { height: '60%', id: 'primary', title: { text: '' } },
    { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
  ],
})

const getRatioChartsSeriesOptions = (name: string[]) => ({
  name,
  yAxis: ['primary', 'secondary', 'secondary'],
})

export namespace MetalsAndMining {
  export namespace SteelChinaIoPrices {
    const CONTEXT_ID = 'equity:metalsandmining:steel_china_io_prices'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'price_hrc'
        | 'price_rebar'
        | 'price_met_coal_china'
        | 'prize_std_mining'
        | 'abs_diff_std_mining'
        | 'std_mining'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'price_hrc':
          case 'price_rebar':
            return getPriceHrcRebarChinaExVatChartProps(config, data)
          case 'price_met_coal_china':
            return getPriceMetCoalChinaChartProps(config, data)
          case 'prize_std_mining':
            return getPrizeStdMiningChartProps(config, data)
          case 'abs_diff_std_mining':
            return getAbsDiffStdMiningChartProps(config, data)
          case 'std_mining':
            return getStdMiningChartProps(config, data)
        }
      },
    }

    const getPriceHrcRebarChinaExVatChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        tooltip: { pointFormatter: magnitudePointFormatter() },
      }
    )

    const getPriceMetCoalChinaChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
    })

    const getPrizeStdMiningChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: idx === 2 ? 'line' : 'area',
        color:
          idx === 1
            ? Palette.fadedGreen
            : idx === 2
            ? Palette.darkGray
            : undefined,
        name: s.name,
        yAxis: idx === 2 ? 'absolute' : 'relative',
        data:
          idx === 2
            ? convertCompassToHighchartsDataFormat(s.data)
            : convertCompassToHighchartsDataFormat(s.data).map(([x, y]) => [
                x,
                100 * y,
              ]),
        ...(idx !== 2
          ? {
              tooltip: {
                valueDecimals: 2,
                valueSuffix: '%',
              },
            }
          : {}),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: true,
          title: { text: 'Preço do minério padrão' },
        },
        {
          id: 'relative',
          opposite: false,
          title: { text: 'Prêmio/desconto' },
          labels: {
            format: '{value}%',
          },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          tooltip: { valueDecimals: 2, xDateFormat: '%A, %b %e, %Y' },
          yAxis,
        },
        stockTools: true,
      }
    }

    const getAbsDiffStdMiningChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: 'area',
        color: idx === 1 ? Palette.fadedGreen : undefined,
        name: s.name,
        yAxis: 'default',
        data: convertCompassToHighchartsDataFormat(s.data),
        tooltip: { valueDecimals: 2 },
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: 'Prêmio (US$/ton)' },
          labels: {
            format: '{value}%',
          },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getStdMiningChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: idx === 0 ? 'column' : 'line',
        color: idx === 0 ? Palette.fadedGreen : Palette.darkBlue,
        name: s.name,
        yAxis: 'default',
        data: convertCompassToHighchartsDataFormat(s.data),
        ...(idx === 0 ? { pointWidth: 30 } : {}),
      }))

      const yAxis = [{ id: 'default', opposite: false }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          tooltip: { shared: true, split: false, valueDecimals: 2 },
          yAxis,
        },
        stockTools: true,
      }
    }
  }

  export namespace IoPortInventoryFreight {
    const CONTEXT_ID = 'equity:metalsandmining:io_port_inventory_freight'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'cn_io_port_inventory'
        | 'cn_io_port_inventory_months'
        | 'freight_qindgao'
        | 'io_shipments_australian'
        | 'io_shipments_brazilian'
        | 'my_steel_miners_op_rate_inventories'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'cn_io_port_inventory':
            return getCnIoPortInventoryChartProps(config, data)
          case 'cn_io_port_inventory_months':
            return getCnIoPortInventoryMonthsChartProps(config, data)
          case 'freight_qindgao':
            return getFreightQindgaoChartProps(config, data)
          case 'io_shipments_australian':
          case 'io_shipments_brazilian':
            return getIoShipmentsChartProps(config, data)
          case 'my_steel_miners_op_rate_inventories':
            return getSteelMinersOpRateInventoriesChartProps(config, data)
        }
      },
    }

    const getCnIoPortInventoryChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [
          {
            id: 'mton',
            opposite: false,
            title: { text: 'mton' },
          },
          {
            id: 'months',
            opposite: true,
            title: { text: 'Months of imported IO' },
          },
        ],
      },
      { yAxis: ['mton', 'months'] }
    )

    const getCnIoPortInventoryMonthsChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(data.data[1].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { min: value }
        }
      )
      convertCompassToHighchartsDataFormat(data.data[2].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
        }
      )

      const series = [
        {
          id: `${config.chart}-2021`,
          type: 'line',
          name: data.data[0].name,
          yAxis: 'default',
          zIndex: 1,
          data: convertCompassToHighchartsDataFormat(data.data[0].data),
        },
        {
          id: `${config.chart}-avg`,
          type: 'line',
          dashStyle: 'LongDash',
          name: data.data[3].name,
          yAxis: 'default',
          zIndex: 1,
          data: convertCompassToHighchartsDataFormat(data.data[3].data),
        },
        {
          id: `${config.chart}-range`,
          type: 'columnrange',
          color: Palette.fadedGreen,
          name: 'Range 2010-2020',
          grouping: false,
          yAxis: 'default',
          xAxis: 0,
          zIndex: 0,
          data: getAreaRangeData(rangeValues),
          pointWidth: 8,
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getFreightQindgaoChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
    })

    const getIoShipmentsChartProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<string, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(
        data.data.find(({ name }) => name.includes('max'))?.data ?? []
      ).forEach((point, idx) => {
        rangeValues[idx] = { min: point[1] }
      })
      convertCompassToHighchartsDataFormat(
        data.data.find(({ name }) => name.includes('min'))?.data ?? []
      ).forEach((point, idx) => {
        rangeValues[idx] = { ...(rangeValues[idx] ?? {}), max: point[1] }
      })

      const series = [
        ...data.data
          .filter(
            ({ name }) =>
              !name.includes('min') &&
              !name.includes('max') &&
              !name.includes('avg')
          )
          .map((s, idx) => ({
            id: `${config.chart}-${idx}`,
            type: 'spline',
            name: s.name,
            tooltip: { valueDecimals: 2 },
            yAxis: 'default',
            zIndex: 1,
            marker: { enabled: false },
            data: convertCompassToHighchartsStrCategoryFormat(s.data),
          })),
        {
          id: `${config.chart}-avg`,
          type: 'spline',
          color: Palette.darkYellow,
          dashStyle: 'LongDash',
          name: capitalizeString(
            data.data
              .find(({ name }) => name.includes('avg'))
              ?.name?.replaceAll('_', ' ') ?? ''
          ),
          tooltip: { valueDecimals: 2 },
          yAxis: 'default',
          zIndex: 1,
          marker: { enabled: false },
          data: convertCompassToHighchartsStrCategoryFormat(
            data.data.find(({ name }) => name.includes('avg'))?.data ?? []
          ),
        },
        {
          id: `${config.chart}-range`,
          type: 'arearange',
          color: Palette.fadedYellow,
          enableMouseTracking: false,
          showInLegend: false,
          grouping: false,
          yAxis: 'default',
          xAxis: 0,
          zIndex: 0,
          marker: { enabled: false },
          data: getAreaRangeData(rangeValues),
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]
      const xAxis = [{ type: 'category', categories: MONTH_LIST_SHORT }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          xAxis,
          yAxis,
        },
        useRegularChart: true,
        stockTools: true,
      }
    }

    const getSteelMinersOpRateInventoriesChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          plotOptions: {
            column: {
              stacking: 'normal',
            },
          },
        },
        {
          type: Array(data.data.length).fill('column'),
        }
      )(config, data)
  }

  export namespace SteelChinaMargin {
    const CONTEXT_ID = 'equity:metalsandmining:steel_china_margin'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'rebar' | 'hrc_crc'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data
        .filter((s) => !s.name?.startsWith('Average'))
        .map((s, idx) => ({
          id: `${config.chart}-${idx}`,
          type: 'line',
          name: s.name,
          yAxis: 'default',
          data: convertCompassToHighchartsDataFormat(s.data),
        }))

      const yAxis = [{ id: 'default', opposite: false }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        defaultIndicators: [
          ...defaultMinMaxIndicators(series),
          ...defaultMSDIndicators(series, {
            avg: true,
            sd1: false,
            sd2: false,
          }),
        ],
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          tooltip: { xDateFormat: '%A, %b %e, %Y' },
          yAxis,
        },
        stockTools: true,
      }
    }
  }

  export namespace SteelChinaProdDemStoExp {
    export const CONTEXT_ID =
      'equity:metalsandmining:steel_china_prod_dem_sto_exp'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'steel_china_production'
        | 'cisa_steelmakers'
        | 'ratio_pig_iron_steel'
        | 'production_growth_pig_iron_steel'
        | 'steel_china_real_demand'
        | 'steel_china_apparent_demand'
        | 'steel_china_stocks'
        | 'rebar_inventories'
        | 'flat_steel_inventories'
        | 'production_days'
        | 'distributor_total_kt'
        | 'mills_inventory_kt'
        | 'steel_china_net_export'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'steel_china_production':
          case 'steel_china_real_demand':
          case 'steel_china_apparent_demand':
          case 'steel_china_net_export':
            return getColumnLineChartProps(config, data)
          case 'cisa_steelmakers':
            return getAreaLineChartProps(config, data)
          case 'ratio_pig_iron_steel':
          case 'production_growth_pig_iron_steel':
            return getMultipleLineChartPropsWrapper(config, data, {
              addMinMaxIndicators: true,
              convertToPercentages: [true, true],
            })
          case 'steel_china_stocks':
            return getMultipleLinesChartProps(config, data)
          case 'rebar_inventories':
          case 'flat_steel_inventories':
            return getMultipleLineChartPropsWrapper(config, data, {
              addMinMaxIndicators: true,
            })
          case 'production_days':
          case 'distributor_total_kt':
          case 'mills_inventory_kt':
            return getMultipleCategoriesChartProps(config, data)
        }
      },
    }

    const getColumnLineChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: idx <= 1 ? 'column' : 'line',
        name: s.name,
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.fadedGreen
            : idx === 2
            ? Palette.darkBlue
            : idx === 3
            ? Palette.darkGreen
            : Palette.darkYellow,
        marker: { enabled: false },
        yAxis: idx <= 1 ? 'absolute' : 'relative',
        data:
          idx <= 1
            ? convertColumnLineDataToHighchartsFormat(s.data)
            : convertColumnLineDataToHighchartsFormat(s.data).map(([x, y]) => [
                x,
                100 * y,
              ]),

        ...(idx > 1
          ? { tooltip: { valueDecimals: 2, valueSuffix: '%' } }
          : { pointPadding: 0, groupPadding: 0 }),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'mt anualizado' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
          xAxis: [{ type: 'category' }],
        },
        stockTools: true,
        useRegularChart: true,
      }
    }

    const convertColumnLineDataToHighchartsFormat = (
      data: CompassAPIDataPoint[]
    ) =>
      data.map(({ date, value }) => {
        if (date.includes('Q')) {
          return [date, value]
        } else {
          const returnDate = new Date(date)
          return [
            `${(returnDate.getMonth() + 1)
              .toString()
              .padStart(2, '0')}/${returnDate.getFullYear()}`,
            value,
          ]
        }
      }) as [string, number][]

    const getMultipleLinesChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: 'spline',
        name: s.name,
        yAxis: 'default',
        data: convertMultipleLinesDataToHighchartsFormat(s.data),
      }))

      const yAxis = [
        { id: 'default', opposite: false, title: { text: 'kt por mês' } },
      ]

      const tooltipFormatter = function () {
        // @ts-ignore
        const _this = this as {
          x: string
          points: {
            y: number
            x: string
            series: {
              name: string
              color: string
            }
          }[]
        }

        const sortedPoints = _this.points.sort((pa, pb) => pb.y - pa.y)
        const minY = sortedPoints[sortedPoints.length - 1].y
        const maxY = sortedPoints[0].y
        const points = sortedPoints.map((point: any) => {
          const isMin = point.y === minY
          const isMax = point.y === maxY
          return `
            <span style="color: ${point.series.color}">●</span>
            ${point.series.name}:
            <span style="font-weight: bold">
              ${point.y.toFixed(1)}${isMax ? ' (max)' : isMin ? ' (min)' : ''}
            </span>
          `
        })
        return `
          <span>${MONTH_LIST_SHORT[new Date(_this.x).getMonth()]}</span>
          <br/>
          ${points.join('<br/>')}
        `
      }

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          tooltip: {
            shared: true,
            split: undefined,
            formatter: tooltipFormatter,
          },
          yAxis,
          xAxis: {
            dateTimeLabelFormats: {
              month: '%b',
              year: '',
            },
          },
        },
        stockTools: true,
      }
    }

    const convertMultipleLinesDataToHighchartsFormat = (
      data: CompassAPIDataPoint[]
    ) => {
      let [day, month] = data[0].date === 'ABR' ? [15, 3] : [1, 0]
      let lastMonth = data[0].date
      const convertedData: [number, number][] = []

      data.forEach(({ date, value }) => {
        if (date === lastMonth) {
          day += 1
        } else {
          month += 1
          day = 1
          lastMonth = date
        }
        convertedData.push([new Date(2020, month, day).getTime(), value])
      })

      return convertedData
    }

    const getAreaLineChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : idx === 2
            ? Palette.darkGreen
            : Palette.darkYellow,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: idx !== 0,
        }),
        name: s.name,
        type: idx === 0 ? 'area' : 'line',
        tooltip: {
          valueDecimals: 2,
          xDateFormat: '%A, %b %e, %Y',
          ...(idx !== 0 ? { valueSuffix: '%' } : {}),
        },
        yAxis: idx === 0 ? 'absolute' : 'relative',
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: '10 days annualized crude steel output (mt)' },
        },
        { id: 'relative', opposite: true, labels: { format: '{value}%' } },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: ChartData,
      chartOptions: any,
      options?: any
    ) => getMultipleLineChartProps(Context, chartOptions, options)(config, data)

    const getMultipleCategoriesChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        color: chartPalette[data.data.length - 1 - idx],
        data: s.data.map((point) => [point.value]),
        enableMouseTracking: idx > data.data.length - 8 ? true : false,
        id: `series-${idx}`,
        lineWidth:
          idx === data.data.length - 1
            ? 4
            : idx > data.data.length - 4
            ? 3
            : 1.25,
        marker: { enabled: false },
        name: s.name,
        tooltip: { valueDecimals: 2 },
        type: 'spline',
        yAxis: 'default',
        zIndex:
          idx === data.data.length - 1
            ? 100
            : idx > data.data.length - 4
            ? 50
            : 10,
      }))

      const yAxis = [{ id: 'default', opposite: false, title: { text: '' } }]
      const xAxis = [
        {
          type: 'category',
          categories: data.data[0].data.map((point) => point.date),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          xAxis,
          yAxis,
        },
        useRegularChart: true,
        stockTools: true,
      }
    }
  }

  export namespace SteelGlobalProduction {
    export const CONTEXT_ID = 'equity:metalsandmining:steel_global_production'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'china'
        | 'world'
        | 'apparent_demand_world_ex_china'
        | 'production_world_ex_china'
        | 'apparent_demand_china'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: idx === 0 || idx === 2 ? 'column' : 'spline',
        name: s.name,
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : idx === 2
            ? Palette.fadedGreen
            : Palette.darkGreen,
        data: convertDataToHighchartsFormat(s.data, {
          convertPercentage: idx === 1 || idx === 3,
        }),
        yAxis: idx === 0 || idx === 2 ? 'absolute' : 'relative',
        ...(idx === 0 || idx === 2
          ? {
              pointPadding: 0,
              groupPadding: 0,
              tooltip: { valueDecimals: 2, valueSuffix: 'bi' },
            }
          : {
              tooltip: { valueDecimals: 2, valueSuffix: '%' },
              marker: { enabled: false },
            }),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Bilhões' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          series,
          yAxis,
          xAxis: { type: 'category' },
        },
        stockTools: true,
        useRegularChart: true,
      }
    }

    const convertDataToHighchartsFormat = (
      data: CompassAPIDataPoint[],
      options?: { convertPercentage: boolean }
    ) =>
      data.map(({ date, value }) => {
        const returnDate = new Date(date)
        const month = returnDate.getMonth() + 1
        const year = returnDate.getFullYear()
        return [
          `${month.toString().padStart(2, '0')}/${year}`,
          options?.convertPercentage ? 100 * value : value,
        ]
      })
  }

  export namespace NickelCopperPrices {
    export const CONTEXT_ID = 'equity:metalsandmining:nickel_copper_prices'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'nickel' | 'copper' | 'coal'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          tooltip: { pointFormatter: magnitudePointFormatter() },
        })(config, data),
    }
  }

  export namespace EvEbitda12MVale {
    export const CONTEXT_ID = 'equity:metalsandmining:ev_ebitda_12m_vale'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ev_ebitda_vale3'
        | 'ev_ebitda_rio3'
        | 'ev_ebitda_ratio_vale3_rio_us'
        | 'ev_ebitda_ratio_vale3_bhp_ln'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ev_ebitda_vale3':
          case 'ev_ebitda_rio3':
            return getValuationChartProps(config, data)
          case 'ev_ebitda_ratio_vale3_rio_us':
            return getRatioChartProps(config, data, 'Ratio Vale/Rio Tinto', [
              'Ratio',
              'Vale',
              'Rio Tinto',
            ])
          case 'ev_ebitda_ratio_vale3_bhp_ln':
            return getRatioChartProps(config, data, 'Ratio Vale/BHP', [
              'Ratio',
              'Vale',
              'BHP',
            ])
        }
      },
    }

    const getValuationChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getRatioChartProps = (
      config: ChartConfig,
      data: ChartData,
      title: string,
      seriesNames: string[]
    ) =>
      getMultipleLineChartProps(
        Context,
        getRatioChartsOptions(title),
        getRatioChartsSeriesOptions(seriesNames)
      )(config, data)
  }

  export namespace SteelExportPrice {
    export const CONTEXT_ID = 'equity:metalsandmining:steel_export_price'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'rebar_turkey' | 'hrc_cn'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
        })(config, data),
    }
  }

  export namespace LongSteelSpread {
    export const CONTEXT_ID = 'equity:metalsandmining:long_steel_spread'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'rebar_us_spread' | 'rebar_turkey_spread' | 'scrap_turkey_spread'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          tooltip: { pointFormatter: magnitudePointFormatter() },
          xAxis: { labels: { format: '{value:%Y}' } },
        })(config, data),
    }
  }

  export namespace BrazilImportParity {
    export const CONTEXT_ID = 'equity:metalsandmining:brazil_import_parity'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'flat_with_premium'
        | 'long'
        | 'flat_parity_to_demand'
        | 'long_parity_to_demand'
        | 'total_imports'
        | 'countries_share_flat'
        | 'countries_share_long'
        | 'countries_share_flat_long'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'countries_share_flat':
          case 'countries_share_long':
          case 'countries_share_flat_long':
            return getStackedPercentageChartProps(config, data)
          default:
            return getMultipleLineChartProps(
              Context,
              {
                ...(!isAbsolute(config.chart)
                  ? { convertToPercentages: Array(data.data.length).fill(true) }
                  : {}),
                addMinMaxIndicators: true,
              },
              { type: Array(data.data.length).fill('spline') }
            )(config, data)
        }
      },
    }

    const isAbsolute = (chart: ChartConfig['chart']) =>
      chart === 'total_imports'

    const getStackedPercentageChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { column: { stacking: 'normal' } },
          tooltip: { xDateFormat: '%B %Y' },
          xAxis: { labels: { format: '{value:%Y}' } },
        },
        { type: Array(data.data.length).fill('column') }
      )(config, formatSeriesNames(data))
  }

  export namespace SteelBrazilDemand {
    export const CONTEXT_ID = 'equity:metalsandmining:steel_brazil_demand'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'flat_apparent_demand'
        | 'flat_apparent_demand_yoy'
        | 'flat_sales_mi'
        | 'flat_sales_mi_yoy'
        | 'long_apparent_demand'
        | 'long_apparent_demand_yoy'
        | 'long_sales_mi'
        | 'long_sales_mi_yoy'
        | 'flat_import_demand'
        | 'long_import_demand'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'flat_apparent_demand':
          case 'flat_sales_mi':
          case 'long_apparent_demand':
          case 'long_sales_mi':
            return getCategoryLinesChartProps(config, data)
          case 'flat_apparent_demand_yoy':
          case 'flat_sales_mi_yoy':
          case 'long_apparent_demand_yoy':
          case 'long_sales_mi_yoy':
            return getMultiLinesChartProps(config, data)
          case 'flat_import_demand':
          case 'long_import_demand':
            return getLineChartProps(config, data)
        }
      },
    }

    const getCategoryLinesChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        color: chartPalette[data.data.length - 1 - idx],
        lineWidth:
          idx === data.data.length - 1
            ? 4
            : idx > data.data.length - 4
            ? 3
            : 1.25,
        id: `${config.chart}-${idx}`,
        type: 'spline',
        name: s.name,
        yAxis: 'default',
        marker: {
          enabled: false,
        },
        data: convertCategoryLinesChartData(s.data),
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: 'kt por mês' },
        },
      ]

      const tooltipFormatter = function () {
        // @ts-ignore
        const _this = this as {
          points: {
            y: number
            key: string
            series: {
              name: string
              color: string
            }
          }[]
        }

        const sortedPoints = _this.points.sort((pa, pb) => pb.y - pa.y)
        const minY = sortedPoints[sortedPoints.length - 1].y
        const maxY = sortedPoints[0].y
        const points = sortedPoints.map((point: any) => {
          const isMin = point.y === minY
          const isMax = point.y === maxY
          return `
            <span style="color: ${point.series.color}">●</span>
            ${point.series.name}:
            <span style="font-weight: bold">
              ${point.y.toFixed(1)}${isMax ? ' (max)' : isMin ? ' (min)' : ''}
            </span>
          `
        })
        return `
          <span>${sortedPoints[0].key}</span>
          <br/>
          ${points.join('<br/>')}
        `
      }

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          series,
          tooltip: {
            shared: true,
            split: undefined,
            formatter: tooltipFormatter,
          },
          yAxis,
          xAxis: [
            {
              type: 'category',
              categories: MONTH_LIST_SHORT,
            },
          ],
        },
        stockTools: true,
        useRegularChart: true,
      }
    }

    const convertCategoryLinesChartData = (data: CompassAPIDataPoint[]) =>
      data.map(({ date, value }) => [date, value])

    const getMultiLinesChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
      convertToPercentages: [true, true],
    })

    const getLineChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
      convertToPercentages: [true],
    })
  }

  export namespace EvEbitdaGerdauUsiminasMitalNucor {
    export const CONTEXT_ID =
      'equity:metalsandmining:ev_ebitda_gerdau_usiminas_mital_nucor'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ev_ebitda_ggbr4'
        | 'ev_ebitda_usim5'
        | 'ev_ebitda_mt_na'
        | 'ev_ebitda_nue_us'
        | 'ev_ebitda_ratio_mt_na_nue_us'
        | 'ev_ebitda_ratio_ggbr4_usim5'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ev_ebitda_ggbr4':
          case 'ev_ebitda_usim5':
          case 'ev_ebitda_mt_na':
          case 'ev_ebitda_nue_us':
            return getValuationChartProps(config, data)
          case 'ev_ebitda_ratio_mt_na_nue_us':
            return getRatioChartProps(config, data, 'Ratio Mital/Nucor', [
              'Ratio',
              'Mital',
              'Nucor',
            ])
          case 'ev_ebitda_ratio_ggbr4_usim5':
            return getRatioChartProps(config, data, 'Ratio Gerdau/Usiminas', [
              'Ratio',
              'Gerdau',
              'Usiminas',
            ])
        }
      },
    }

    const getValuationChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string,
      seriesNames: string[]
    ) =>
      getMultipleLineChartProps(
        Context,
        getRatioChartsOptions(title),
        getRatioChartsSeriesOptions(seriesNames)
      )(config, data)
  }

  export namespace ChinaCeic {
    export const CONTEXT_ID = 'equity:metalsandmining:china_ceic'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'floor_space_started'
        | 'floor_space_started_yoy'
        | 'floor_space_sold'
        | 'floor_space_sold_yoy'
        | 'land_area_purchased'
        | 'land_area_purchased_yoy'
        | 'tsf_growth_yoy'
        | 'china_pmi'
        | 'auto_sales_yoy'
        | 'fai_infra_china'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'floor_space_started':
          case 'floor_space_sold':
          case 'land_area_purchased':
            return getCategoriesChartProps(config, data)
          case 'floor_space_started_yoy':
          case 'floor_space_sold_yoy':
          case 'land_area_purchased_yoy':
            return getColumnLineChartProps(config, data)
          case 'tsf_growth_yoy':
          case 'auto_sales_yoy':
            return getMultiLineChartProps(config, data)
          case 'china_pmi':
          case 'fai_infra_china':
            return getLineChartProps(config, data)
        }
      },
    }

    const getCategoriesChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: idx === 0 || idx === 2 ? 'column' : 'spline',
        name: s.name,
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : idx === 2
            ? Palette.fadedGreen
            : Palette.darkGreen,
        yAxis: idx === 0 || idx === 2 ? 'absolute' : 'relative',
        data: convertDataToHighchartsFormat(s.data, {
          convertPercentage: idx === 1 || idx === 3,
        }),
        ...(idx === 0 || idx === 2
          ? {
              pointPadding: 0,
              groupPadding: 0,
            }
          : {
              tooltip: {
                valueDecimals: 2,
                valueSuffix: '%',
              },
              marker: {
                enabled: false,
              },
            }),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Bilhões' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          series,
          yAxis,
          xAxis: { type: 'category' },
        },
        stockTools: true,
        useRegularChart: true,
      }
    }

    const convertDataToHighchartsFormat = (
      data: CompassAPIDataPoint[],
      options?: { convertPercentage: boolean }
    ) =>
      data.map(({ date, value }) => {
        const returnDate = new Date(date)
        const month = returnDate.getMonth() + 1
        const year = returnDate.getFullYear()
        return [
          `${month.toString().padStart(2, '0')}/${year}`,
          options?.convertPercentage ? 100 * value : value,
        ]
      })

    const getColumnLineChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: idx === 0 ? 'column' : 'line',
        name: s.name,
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : Palette.darkGreen,
        yAxis: idx === 0 ? 'absolute' : 'relative',
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: idx !== 0,
        }),
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: idx === 0 ? '' : '%',
          }),
          xDateFormat: '%A, %b %e, %Y',
        },
        ...(idx === 0 ? { pointPadding: 0, groupPadding: 0 } : {}),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Milhão m²' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        defaultIndicators: defaultMinMaxIndicators(series.slice(1)),
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getMultiLineChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
      convertToPercentages: [true, true],
    })

    const getLineChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: [config.chart === 'fai_infra_china'],
      })(config, data)
  }

  export namespace ChinaPmi {
    export const CONTEXT_ID = 'equity:metalsandmining:china_pmi'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'china_pmi'
        | 'china_pmi_output'
        | 'china_pmi_new_orders'
        | 'china_pmi_new_export_orders'
        | 'china_pmi_inventory_finished_goods'
        | 'china_pmi_raw_material'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: 'line',
        color: idx === 1 ? Palette.darkBlue : Palette.gray,
        name: s.name,
        yAxis: 'default',
        data: convertCompassToHighchartsDataFormat(s.data),
        enableMouseTracking: idx === 1,
        dashStyle: idx === 1 ? 'Solid' : 'ShortDash',
        showInLegend: idx === 1,
        tooltip: { xDateFormat: '%B %Y' },
      }))

      const yAxis = [{ id: 'default', opposite: false }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        defaultIndicators: defaultMinMaxIndicators(series.slice(1)),
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }
  }

  export namespace FurnaceUtilization {
    export const CONTEXT_ID = 'equity:metalsandmining:furnace_utilization'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'china_blast_furnace_capacity_utilization'
        | 'china_eaf_capacity_utilization'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          convertToPercentages: [true],
          xAxis: { labels: { format: '{value:%Y}' } },
        })(config, data),
    }
  }

  export namespace UsSteelMill {
    export const CONTEXT_ID = 'equity:metalsandmining:us_steel_mill'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'rscurate' | 'lead_time'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        ...(config.chart === 'rscurate'
          ? { convertToPercentages: [true] }
          : {}),
      })(config, data)
  }

  export namespace Bonds {
    export const CONTEXT_ID = 'equity:metalsandmining:bonds'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'mining' | 'steel'
      series: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url: '/api/charts/metals-and-mining/bonds/' + config.chart,
        method: 'POST',
        data: { series: config.series },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config, {
          unorderedArrays: true,
        })}`,
      getChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: {
            xDateFormat: '%A, %b %e, %Y',
          },
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)
  }
}
